import { Button, type ButtonProps } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";

import { useFormContext } from "./form-context";

export type UpdateButtonMode = "dirty" | "touched" | "defaultValue";

export type UpdateButtonProps = ButtonProps & {
	label?: string;
	isLoading?: boolean;
	children?: React.ReactNode;
	/** Controls when the button becomes enabled:
	 * - `"defaultValue"` (default) — enabled when values differ from defaults (non-persistent)
	 * - `"dirty"` — enabled once any field has been changed (persistent, stays enabled even if reverted)
	 * - `"touched"` — enabled once any field has been focused and blurred
	 */
	mode?: UpdateButtonMode;
};

export function UpdateButton({
	label = "更新",
	isLoading,
	className,
	children,
	mode = "dirty",
	...props
}: UpdateButtonProps) {
	const form = useFormContext();

	return (
		<form.Subscribe
			selector={(state) => [
				state.canSubmit,
				state.isSubmitting,
				state.isDirty,
				state.isTouched,
				state.isDefaultValue,
			]}
		>
			{([canSubmit, isSubmitting, isDirty, isTouched, isDefaultValue]) => {
				const hasChanges =
					mode === "dirty" ? isDirty : mode === "touched" ? isTouched : !isDefaultValue;

				return (
					<Button
						className={className}
						disabled={!canSubmit || !hasChanges}
						type="submit"
						{...props}
					>
						{children || (isSubmitting || isLoading ? <Spinner /> : label)}
					</Button>
				);
			}}
		</form.Subscribe>
	);
}
